import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { ApexAxisChartSeries, ApexChart, ApexPlotOptions, ApexXAxis, ApexDataLabels, ApexTooltip, ApexResponsive } from 'ng-apexcharts';
import { FiltroPrimeiroContato, MotivoAgrupado, MotivoRaw, PrimeiroContato } from '../interfaces/primeiro-contato';
import { Router } from '@angular/router';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  plotOptions: ApexPlotOptions;
  xaxis: ApexXAxis;
  dataLabels: ApexDataLabels;
  tooltip: ApexTooltip;
  responsive: ApexResponsive[];
};

@Component({
  selector: 'app-primeiro-contato',
  templateUrl: './primeiro-contato.component.html',
  styleUrls: ['./primeiro-contato.component.scss']
})
export class PrimeiroContatoComponent implements OnInit {

  @Input() primeiroContato!: PrimeiroContato;
  @Input() filtro: FiltroPrimeiroContato;
  chartOptions: Partial<ChartOptions>;
  labelsEsperados = [
    "Até 5 minutos",
    "30 minutos",
    "2 horas",
    "1 dia",
    "2 dias",
    "Acima de dois dias",
    "Pendentes"
  ];

  constructor(private router: Router) { }

  ngOnInit(): void {
    this.inicializarGrafico();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['primeiroContato'] && changes['primeiroContato'].currentValue) {
      this.inicializarGrafico();
    }
  }

  onBarClick(dataPointIndex: number) {
    const agrupado = this.agruparMotivos(this.primeiroContato.motivos);
    const leadPorLabel = this.gerarLeadPorLabel(agrupado);
    let idLead = []

    if (dataPointIndex + 1 === 1) {
      idLead = leadPorLabel["Até 5 minutos"] || [];
    } else if (dataPointIndex + 1 === 2) {
      idLead = leadPorLabel["30 minutos"] || [];
    } else if (dataPointIndex + 1 === 3) {
      idLead = leadPorLabel["2 horas"] || [];
    } else if (dataPointIndex + 1 === 4) {
      idLead = leadPorLabel["1 dia"] || [];
    } else if (dataPointIndex + 1 === 5) {
      idLead = leadPorLabel["2 dias"] || [];
    } else {
      idLead = leadPorLabel["Acima de dois dias"] || [];
    }

    this.router.navigate(['/veiculo/leads'],
      {
        queryParams: {
          dataInic: this.filtro.data_inicial,
          dataFim:this.filtro.data_final,
          idLead: [idLead],
          btVoltar: true
        },
      }
    );
  }

  private inicializarGrafico(): void {
    if (!this.primeiroContato) { return; }

    let agrupado = this.agruparMotivos(this.primeiroContato.motivos);
    agrupado = this.completarComZero(agrupado, this.labelsEsperados);
    agrupado = this.ordenarPorLabelsPadrao(agrupado, this.labelsEsperados);
    const labels = this.extrairLabels(agrupado);
    const valores = this.extrairValores(agrupado);

    const total = this.somarTotalValores(agrupado);

    this.chartOptions = {
      series: [{
        name: 'Motivos',
        data: valores
      }],
      chart: {
        type: 'bar',
        height: labels.length * 40,
        toolbar: { show: false },
        events: {
          dataPointSelection: (_event, _chartContext, config) => {
            this.onBarClick(config.dataPointIndex);
          }
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '50%'
        }
      },
      tooltip: {
        y: {
          formatter: (val: number) => {
            const percent = ((val / total) * 100).toFixed(1);
            return `${val} (${percent}%)`;
          }
        }
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px'
        }
      },
      xaxis: {
        categories: labels,
        tickAmount: 4,
        labels: {
          style: {
            fontSize: '11px'
          }
        }
      },
      responsive: [{
        breakpoint: 768,
        options: {
          chart: {
            height: labels.length * 50
          },
          plotOptions: {
            bar: {
              barHeight: '45%'
            }
          },
          xaxis: {
            tickAmount: 3,
            labels: {
              style: { fontSize: '10px' }
            }
          },
          dataLabels: {
            style: { fontSize: '10px' }
          }
        }
      }]
    };
  }

  public agruparMotivos(motivos: MotivoRaw[]): MotivoAgrupado[] {
    const agrupadoObj = motivos.reduce((acc, cur) => {
      if (!acc[cur.label]) {
        acc[cur.label] = {
          label: cur.label,
          valor: 0,
          id_lead: []
        };
      }
      acc[cur.label].valor += cur.valor;
      acc[cur.label].id_lead.push(cur.id_lead);
      return acc;
    }, {} as { [label: string]: MotivoAgrupado });

    return Object.values(agrupadoObj);
  }

  public extrairLabels(agrupado: MotivoAgrupado[]): string[] {
    return agrupado.map(item => item.label);
  }

  public extrairValores(agrupado: MotivoAgrupado[]): number[] {
    return agrupado.map(item => item.valor);
  }

  public extrairLeadPorLabel(agrupado: MotivoAgrupado[]): number[][] {
    return agrupado.map(item => item.id_lead);
  }

  gerarLeadPorLabel(agrupado: MotivoAgrupado[]): { [label: string]: number[] } {
    const resultado: { [label: string]: number[] } = {};
    agrupado.forEach(item => {
      resultado[item.label] = item.id_lead;
    });
    return resultado;
  }

  completarComZero(agrupado: MotivoAgrupado[], labelsPadrao: string[]): MotivoAgrupado[] {
    const existente = new Set(agrupado.map(m => m.label));
  
    labelsPadrao.forEach(label => {
      if (!existente.has(label)) {
        agrupado.push({
          label: label,
          valor: 0,
          id_lead: []
        });
      }
    });
  
    return agrupado;
  }  

  ordenarPorLabelsPadrao(agrupado: MotivoAgrupado[], labelsPadrao: string[]): MotivoAgrupado[] {
    return agrupado.sort((a, b) => {
      return labelsPadrao.indexOf(a.label) - labelsPadrao.indexOf(b.label);
    });
  }

  somarTotalValores(agrupado: MotivoAgrupado[]): number {
    return agrupado.reduce((total, item) => total + item.valor, 0);
  }
}
